import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

(pdfMake as any).vfs = (pdfFonts as any)?.pdfMake?.vfs || pdfFonts;

const labelColor = '#667085';
const valueColor = '#101828';
const lineColor = '#EAECF0';
const headerFill = '#F9FAFB';
const sectionColor = '#667085';
const borderColor = '#D0D5DD';
const labelSize = 8;
const valueSize = 10;
const sectionSize = 10;
const labelColumn = 118;

export async function downloadOnCallBillingPdf(data: any, _statusLabel?: string): Promise<void> {
  const slip = data || {};
  const refNo = text(slip.referenceNumber);
  const signature = await signatureImage(slip);
  const logo = await logoSvg();
  const fileName = 'Billing Report.pdf';
  const download = (image: string | null, brand: string | null) => {
    const result = pdfMake.createPdf(buildDutySlip(slip, refNo, image, brand)).download(fileName);
    return result && typeof (result as any).then === 'function' ? result : Promise.resolve();
  };
  try {
    await download(signature, logo);
  } catch {
    if (signature || logo) {
      await download(null, null);
    }
  }
}

function logoSvg(): Promise<string | null> {
  return fetch('assets/images/atom-logo.svg')
    .then(response => response.ok ? response.text() : null)
    .catch(() => null);
}

function signatureImage(slip: any): Promise<string | null> {
  const raw = slip?.signatureFileUrl == null ? '' : String(slip.signatureFileUrl).trim();
  if (!raw || raw.toLowerCase() === 'na' || raw.toLowerCase() === 'not available' || /^https?:\/\//i.test(raw)) {
    return Promise.resolve(null);
  }
  if (/^data:image\/(png|jpe?g);base64,/i.test(raw)) {
    return Promise.resolve(raw);
  }
  if (raw.startsWith('/9j/')) {
    return Promise.resolve('data:image/jpeg;base64,' + raw);
  }
  if (raw.startsWith('iVBORw0KGgo')) {
    return Promise.resolve('data:image/png;base64,' + raw);
  }
  return Promise.resolve(null);
}

function buildDutySlip(slip: any, refNo: string, signature: string | null, logo: string | null): any {
  return {
    pageSize: 'A4',
    pageMargins: [32, 28, 32, 36],
    background: () => ({
      canvas: [{
        type: 'rect',
        x: 16,
        y: 16,
        w: 563,
        h: 810,
        lineWidth: 1.2,
        lineColor: borderColor
      }]
    }),
    footer: (currentPage: number, pageCount: number) => ({
      margin: [32, 0, 32, 12],
      columns: [
        { text: `Generated on ${generatedOn()}`, fontSize: labelSize, color: labelColor },
        { text: `Page ${currentPage} of ${pageCount}`, fontSize: labelSize, color: labelColor, alignment: 'right' }
      ]
    }),
    content: [
      {
        columns: [
          logo
            ? { svg: logo, width: 130, margin: [0, 4, 0, 0] }
            : { text: '', width: 130 },
          {
            width: '*',
            text: [
              { text: 'Ref No: ', bold: true, fontSize: 11, color: valueColor },
              { text: refNo, fontSize: 11, color: '#FF455B' }
            ],
            alignment: 'right',
            margin: [0, 8, 0, 0]
          }
        ],
        columnGap: 12,
        margin: [0, 4, 0, 0]
      },
      { canvas: [{ type: 'line', x1: 0, y1: 10, x2: 531, y2: 10, lineWidth: 0.8, lineColor }] },
      sectionTitle('Request and Trip', [0, 14, 0, 2]),
      {
        stack: [
          fieldRows([
            [['Request No', text(slip.requestNumber)], ['Request Date', text(slip.requestDate)]],
            [['Trip Code', text(slip.tripCode)], ['Booking Type', text(slip.bookingType)]],
            [['Trip Date', text(slip.tripDate)], ['Reporting Time', text(slip.reportingTime)]]
          ]),
          {
            margin: [0, 8, 0, 0],
            stack: [
              fieldRows([
                [['Requester Name', text(slip.requesterName)]],
                [['PRO Name', text(slip.proName)]]
              ]),
              fieldRows([
                [['PRO Location & Site', proLocationSite(slip)]],
                [['Pick up', text(slip.fromLocation)]],
                [['Drop', text(slip.toLocation)]],
                [['Booked City', text(slip.bookingCity)]],
                [['Instruction', text(slip.reason)]]
              ])
            ]
          },
          {
            margin: [0, 8, 0, 0],
            stack: [
              fieldRows([
                [['Passenger Count', text(slip.passengerCount)], ['Actual KM', readingKm(slip.actualKm)]]
              ]),
              passengerNameSection(slip),
              fieldRows([
                [['Duration', text(slip.duration)]]
              ])
            ]
          }
        ]
      },
      sectionTitle('Vehicle and Vendor', [0, 14, 0, 2]),
      fieldRows([
        [['Vendor Name', text(slip.vendorName)]],
        [['Driver Name', text(slip.driverName)]],
        [['Vehicle No', text(slip.vehicleNumber)], ['Vehicle Type', text(slip.vehicleType)]],
        [['Fuel Type', text(slip.fuelType)]]
      ]),
      sectionTitle('Billing', [0, 14, 0, 2]),
      fieldRows([
        [['Company Name', text(slip.gstCorporateName)]],
        [['Billing Entity', text(slip.billingEntity)]],
        [['Cost Centre', text(slip.costCenter)], ['Billing Responsibility', text(slip.billingResponsibility)]]
      ]),
      {
        unbreakable: true,
        stack: [
          sectionTitle('Odometer Readings and Times', [0, 14, 0, 8]),
          odometerTable(slip)
        ]
      },
      {
        unbreakable: true,
        stack: [
          sectionTitle('Employee Signature', [0, 16, 0, 8]),
          {
            columns: [
              {
                width: '62%',
                table: {
                  widths: ['*'],
                  body: [[signature
                    ? { image: signature, fit: [260, 72], alignment: 'center', margin: [8, 8, 8, 8] }
                    : { text: '', margin: [8, 28, 8, 28] }]]
                },
                layout: dashedBox
              },
              { width: '38%', text: '' }
            ]
          }
        ]
      }
    ],
    defaultStyle: { font: 'Roboto' }
  };
}

const noLines = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0
};

const dashedBox = {
  hLineWidth: () => 0.8,
  vLineWidth: () => 0.8,
  hLineColor: () => borderColor,
  vLineColor: () => borderColor,
  hLineStyle: () => ({ dash: { length: 3, space: 2 } }),
  vLineStyle: () => ({ dash: { length: 3, space: 2 } })
};

function fieldRows(rows: string[][][]) {
  return {
    stack: rows.map(row => {
      const singleLine = row.length === 1;
      return {
        unbreakable: true,
        columns: (singleLine ? [0] : [0, 1]).map(index => ({
          width: singleLine ? '100%' : '50%',
          stack: row[index] ? [fieldBlock(row[index][0], row[index][1], singleLine)] : [{ text: '' }]
        }))
      };
    })
  };
}

function odometerTable(slip: any) {
  const header = ['READING POINT', 'ODOMETER READING', 'TIME'].map(title => ({
    text: title,
    bold: true,
    fontSize: labelSize,
    color: '#475467',
    fillColor: headerFill,
    margin: [8, 7, 8, 7]
  }));
  const rows = [
    ['Trip Start', readingKm(slip.odometerStart), text(slip.tripStartTime)],
    ['Employee Boarding', readingKm(slip.boardedOdometerReading), text(slip.boardedTime)],
    ['Employee De-Boarding', readingKm(slip.deboardedOdometerReading), text(slip.deboardedTime)],
    ['Trip Stop', readingKm(slip.odometerStop), text(slip.tripStopTime)]
  ];
  return {
    table: {
      widths: ['*', 140, 120],
      body: [
        header,
        ...rows.map(row => row.map(cell => ({
          text: cell,
          fontSize: valueSize,
          color: valueColor,
          margin: [8, 7, 8, 7]
        })))
      ]
    },
    layout: {
      hLineColor: () => lineColor,
      vLineColor: () => lineColor,
      hLineWidth: () => 0.6,
      vLineWidth: () => 0.6,
      fillColor: (rowIndex: number) => rowIndex === 0 ? headerFill : null
    }
  };
}

function sectionTitle(value: string, margin: number[]) {
  return {
    margin,
    stack: [
      sectionHeading(value),
      sectionLine()
    ]
  };
}

function sectionHeading(value: string) {
  return {
    text: value,
    fontSize: sectionSize,
    color: sectionColor,
    margin: [0, 0, 0, 4]
  };
}

function sectionLine() {
  return {
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 531, y2: 0, lineWidth: 0.7, lineColor }]
  };
}

function fieldBlock(label: string, value: string, fullWidth = false) {
  return {
    margin: [0, 7, fullWidth ? 0 : 10, 0],
    table: {
      widths: [labelColumn, '*'],
      body: [[
        { text: label, fontSize: labelSize, color: labelColor, margin: [0, 2, 6, 0] },
        { text: value, bold: true, fontSize: valueSize, color: valueColor, margin: [0, 1, 0, 0] }
      ]]
    },
    layout: noLines
  };
}

function passengerNameSection(slip: any) {
  const passengers = passengerEntries(slip);
  const lines = passengers.length
    ? passengers.map(entry => entry.empId ? `${entry.name} (Emp ID: ${entry.empId})` : (entry.name || 'Not Available'))
    : [text(slip?.passengerName)];
  return {
    margin: [0, 7, 0, 0],
    table: {
      widths: [labelColumn, '*'],
      body: [[
        { text: 'Passenger Name', fontSize: labelSize, color: labelColor, margin: [0, 2, 6, 0] },
        {
          stack: lines.map((line, index) => ({
            text: line,
            bold: true,
            fontSize: valueSize,
            color: valueColor,
            margin: [0, index === 0 ? 1 : 4, 0, 0]
          }))
        }
      ]]
    },
    layout: noLines
  };
}

function passengerEntries(slip: any): { name: string; empId: string }[] {
  const passengers = Array.isArray(slip?.passengers) ? slip.passengers : [];
  return passengers.map((passenger: any) => ({
    name: passenger?.name == null ? '' : String(passenger.name).trim(),
    empId: passenger?.empId == null ? '' : String(passenger.empId).trim()
  })).filter(passenger => passenger.name || passenger.empId);
}

function text(value: any): string {
  const raw = value == null ? '' : String(value).replace(/\s+/g, ' ').trim();
  if (!raw || raw.toLowerCase() === 'na') {
    return 'Not Available';
  }
  return raw;
}

function readingKm(value: any): string {
  const raw = text(value);
  if (raw === 'Not Available') {
    return raw;
  }
  return raw.toLowerCase().endsWith('km') ? raw : `${raw} km`;
}

function proLocationSite(slip: any): string {
  const location = locationPart(slip?.prolocation);
  const site = locationPart(slip?.prosite);
  if (location && site) {
    return location + ' | ' + site;
  }
  return location || site || 'Not Available';
}

function locationPart(value: any): string {
  const raw = text(value);
  return raw === 'Not Available' ? '' : raw;
}

function generatedOn(): string {
  return new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

